import { useLazyQuery, useQuery } from '@apollo/client';
import { Box, DialogActions, MenuItem, Select, SelectChangeEvent, styled } from '@mui/material';
import LanguageSelectorItem from 'Components/Forms/LanguageSelectorItem';
import Loader from 'Components/Forms/Loader';
import FlexBox from 'Components/FlexBox';
import { DialogDefault, InfoTooltip, ThemeButton } from 'DesignSystem';
import { ELanguage } from 'DesignSystem/types/language.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { ImageVariant } from 'FeatureModules/Media/types/media.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { StyledDialogContent } from 'FeatureModules/ProductPage/Components/Create/styles';
import { InlineHelperText } from 'FeatureModules/ProductPage/Components/InlineHelperText/InlineHelperText';
import {
  farmSchema,
  siteCreationSchema,
  siteEditingSchema,
} from 'FeatureModules/Site/schemas/sites.schema';
import {
  ELandOwnership,
  ESiteType,
  IBaseSite,
  IFarmSite,
  ISite,
} from 'FeatureModules/Site/types/site.types';
import { Form, Formik, FormikProps } from 'formik';
import {
  ICreateSiteInput,
  IUpdateSiteInput,
} from 'graphql/secure/mutations/types/site-mutation.types';
import { IS_EXTERNAL_ID_AVAILABLE } from 'graphql/secure/queries/company.queries';
import { GET_SITE } from 'graphql/secure/queries/site.queries';
import useConfig from 'hooks/useConfig';
import useDialog from 'hooks/useDialog';
import isEqual from 'lodash/isEqual';
import round from 'lodash/round';
import React, { FC, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { ICoordinates } from 'types/map.types';
import { mergeDefaults } from 'utils/form.utils';
import useSiteMutations from '../../hooks/useSiteMutations';
import SiteForm from '../Forms/SiteForm';

interface IAddEditSiteDialogProps extends IDefaultDialogProps {
  /** If passed its editing the existing site */
  site?: IBaseSite;
  /**
   * Used for creating a site in a cluster
   */
  defaultSiteType?: ESiteType;
  defaultRawMaterialId?: string;
  defaultCountry?: string;
  /**
   * Always assigns the site to this partner
   */
  ownedBy?: IBasePartnerCompany;
  /**
   * Will connect the site to this site cluster
   */
  siteClusterId?: string;

  hidePartnerSelector?: boolean;
  /**
   * Callback function that is called when a site is created
   */
  onSiteCreated?: (site: IBaseSite) => void;
}

interface IFarmValues extends IFarmSite {
  rawMaterialId: string;
  sizeHa?: number;
  numberOfFarmers?: number;
  weight?: number;
  additionalRawMaterialIds?: string[];
  landOwnership?: ELandOwnership;
  farmOwner?: string;
}

export interface IDefaultFormValues {
  siteType: ESiteType;
  title: string;
  locationCoordinates: ICoordinates;
  locationName: string;
  mapboxId: string;
  image?: ImageVariant;
  ownedBy?: IBasePartnerCompany;
  externalId?: string;
  description?: string;
  /**
   * If locationCoordinates are manually set, we do not send the mapboxId to the backend and the BE itself will do reverse geocoding
   * This flag depends on were the user sets the coordinates from (address search or manual input)
   */
  useExactCoordinates: boolean;
}

export type TFarmFormValues = IDefaultFormValues & {
  siteType: ESiteType.FARM;
  farmData: IFarmValues;
};

export type TFormValues =
  | (IDefaultFormValues & {
      siteType: Exclude<ESiteType, ESiteType.FARM>;
    })
  | TFarmFormValues;

const StyledDialogActions = styled(DialogActions)<{ 'has-shadow': Tbooleanish }>(
  ({ theme, 'has-shadow': hasShadow }) => ({
    padding: theme.spacing(3, 5, 4),
    justifyContent: 'space-between',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,

    ...(hasShadow === 'true' && {
      padding: theme.spacing(3, 5),
      background: theme.custom.themeColors.white,
      boxShadow: theme.custom.shadows[4],
    }),
  })
);

const StyledSelect = styled(Select)(() => ({
  '& .MuiSelect-select ': {
    padding: '6px 12px !important',
  },

  '& .MuiButtonBase-root': {
    padding: 0,
    paddingRight: 20,
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  '& .MuiButtonBase-root': {
    padding: 0,
    paddingRight: 20,
  },
}));

const TopBackground = styled('div')(({ theme }) => ({
  '&::before': {
    content: '""',
    position: 'absolute',
    zIndex: 0,
    top: 0,
    left: 0,
    right: 0,
    height: '42%',
    background: theme.custom.colors.backgroundLight,
  },
}));

const AddEditSiteDialog: FC<IAddEditSiteDialogProps> = ({
  site: siteProp,
  ownedBy,
  open,
  siteClusterId,
  defaultSiteType,
  defaultRawMaterialId,
  defaultCountry,
  hidePartnerSelector,
  onClose,
  onSiteCreated,
}) => {
  const navigate = useNavigate();
  const { createSite, updateSite } = useSiteMutations();
  const { openDialog } = useDialog();
  const { productLanguages } = useConfig();
  const [selectedLanguage, setSelectedLanguage] = useState<ELanguage>(ELanguage.ENGLISH);

  const { data: siteData, loading } = useQuery<{
    site: ISite;
  }>(GET_SITE, {
    variables: { id: siteProp?.id, lang: selectedLanguage },
    skip: selectedLanguage === ELanguage.ENGLISH || !siteProp?.id,
    fetchPolicy: 'no-cache', // don't store in cache because it should not overwrite the componentOverview in the background
  });

  const [isExternalIdAvailable, { data, variables, loading: isExternalIdAvailableLoading }] =
    useLazyQuery<{
      isExternalIdAvailable: boolean;
    }>(IS_EXTERNAL_ID_AVAILABLE, {
      variables: {
        model: 'Site',
        filters: {},
      },
      notifyOnNetworkStatusChange: true,
    });

  const site = selectedLanguage === ELanguage.ENGLISH ? siteProp : siteData?.site;

  const isEdit = site !== undefined;

  const formRef = useRef<FormikProps<TFormValues>>(null);
  const initialValues = mergeDefaults(siteEditingSchema.default(), {
    title: site?.title,
    image: site?.image,
    ownedBy: ownedBy || site?.ownedBy,
    siteType: defaultSiteType || site?.siteType,
    locationCoordinates: site?.locationCoordinates,
    locationName: site?.locationName,
    externalId: site?.externalId,
    description: site?.description,
    farmData:
      site?.siteType === ESiteType.FARM || defaultSiteType === ESiteType.FARM
        ? mergeDefaults(
            farmSchema.default(),
            !!site && site.siteType === ESiteType.FARM
              ? {
                  rawMaterialId: site?.rawMaterial?.id,
                  sizeHa: site?.sizeHa ? round(site?.sizeHa, 3) : undefined,
                  numberOfFarmers: site?.numberOfFarmers,
                  additionalRawMaterialIds: site?.additionalRawMaterialIds,
                  landOwnership: site?.landOwnership,
                  farmOwner: site?.farmOwner,
                }
              : {
                  rawMaterialId: defaultRawMaterialId,
                }
          )
        : undefined,
  });

  const handleSubmit = async (values: TFormValues) => {
    if (!isEdit) {
      const input: ICreateSiteInput = {
        siteType: values.siteType,
        title: values.title,
        image: values.image?.id ? { imageId: values.image.id } : undefined,
        ownedById: values.ownedBy?.id || null,
        ...(values.useExactCoordinates
          ? { locationCoordinates: values.locationCoordinates }
          : { mapboxId: values.mapboxId }),
        externalId: values.externalId || null,
        description: values.description,
        ...(values.siteType !== ESiteType.CLUSTER && siteClusterId ? { siteClusterId } : {}),
        ...(values.siteType === ESiteType.FARM ? { farmInput: values.farmData } : {}),
      };
      const siteCreationResponse = await createSite({
        variables: { input, lang: selectedLanguage },
      });
      if (siteCreationResponse.data?.createSite.site) {
        const site = siteCreationResponse.data.createSite.site;
        if (onSiteCreated) {
          onSiteCreated(site);
        } else {
          navigate(`/sites/${site.id}`);
        }
      }
    } else {
      const input: IUpdateSiteInput = {
        siteType: values.siteType,
        title: values.title,
        image: values.image?.id ? { imageId: values.image.id } : null,
        ownedById: values.ownedBy?.id || null,
        ...(values.useExactCoordinates
          ? { locationCoordinates: values.locationCoordinates }
          : { mapboxId: values.mapboxId }),
        externalId: values.externalId || null,
        description: values.description || null,
        ...(values.siteType !== ESiteType.CLUSTER && siteClusterId ? { siteClusterId } : {}),
        ...(values.siteType === ESiteType.FARM
          ? {
              farmInput: {
                ...values.farmData,
                sizeHa: !!values.farmData.sizeHa ? values.farmData.sizeHa : null,
              },
            }
          : {}),
      };
      await updateSite({ variables: { id: site.id, input, lang: selectedLanguage } });
    }
    onClose?.();
  };

  // If there are unsaved changes, ask the user if they want to leave the page
  const handleClose = () => {
    if (formRef.current && !isEqual(formRef.current.values, initialValues)) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          submitText: 'Discard changes',
          itemTitle: formRef.current?.values?.title,
          displayCloseButton: true,
          onSubmit: onClose,
          onCancel: () => undefined,
        },
      });
    } else {
      onClose?.();
    }
  };

  /**
   * Ideally we would debounce this function, but for some reason this messes up the state is messed up and always one step behind
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const customExternalIdValidation = useCallback(
    async (values: TFormValues) => {
      if (
        !values.externalId ||
        (initialValues.externalId === values.externalId &&
          initialValues.ownedBy?.id === values.ownedBy?.id)
      ) {
        // if the value is empty or the same as the initial value, do not check availability and clear errors
        return undefined;
      }
      // Check if the query is different from the previous one and use the existing value
      if (
        !!data &&
        !!variables &&
        variables.externalId === values.externalId &&
        variables.filters.ownedById === values.ownedBy?.id
      ) {
        return data.isExternalIdAvailable === true
          ? undefined
          : { externalId: 'The ID already exists. Enter a unique ID.' };
      }
      try {
        const response = await isExternalIdAvailable({
          variables: {
            externalId: values.externalId,
            filters: {
              ownedById: values.ownedBy?.id,
            },
          },
        });
        return response.data?.isExternalIdAvailable === true
          ? undefined
          : { externalId: 'The ID already exists. Enter a unique ID.' };
      } catch (error) {
        return { externalId: 'Error checking id availability' };
      }
    },
    [data, initialValues, isExternalIdAvailable, variables]
  );

  const handleLanguageChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedLanguage(event.target.value as ELanguage);
  };

  return (
    <DialogDefault
      title={!site ? 'Add new site' : 'Edit Site'}
      data-testid="add-edit-site-dialog"
      open={open}
      fullWidth
      iconName={!site ? 'plus' : 'edit'}
      maxWidth="md"
      onClose={handleClose}
    >
      <TopBackground />

      <Box zIndex={2}>
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          enableReinitialize
          validateOnChange
          validationSchema={isEdit ? siteEditingSchema : siteCreationSchema}
          isInitialValid={false}
          initialStatus={{
            lang: selectedLanguage,
          }}
          onSubmit={handleSubmit}
          validate={customExternalIdValidation}
        >
          {({ values, isSubmitting, isValid }) => (
            <Form>
              {loading && <Loader overlayOpacity />}
              <FlexBox position="absolute" top={20} right={54} zIndex={9}>
                <InfoTooltip
                  size={EAvailableSizes.SMALL}
                  text="Add and edit the site information in different languages. This can also be managed in the product page communication. Adding a new language automatically auto-translates from the default language."
                />
                <StyledSelect
                  label="Component name"
                  variant="outlined"
                  value={selectedLanguage}
                  onChange={handleLanguageChange}
                >
                  {productLanguages.map(
                    ({ title, key, flag }: { title: string; key: string; flag: string }) => (
                      <StyledMenuItem key={`product-lang-${key}`} value={key}>
                        <LanguageSelectorItem
                          value={key}
                          flag={flag}
                          title={title}
                          hoverBackground={false}
                          noPadding={undefined}
                        />
                      </StyledMenuItem>
                    )
                  )}
                </StyledSelect>
              </FlexBox>

              <StyledDialogContent>
                <Box display="flex" alignItems="center" mb={4} position="relative">
                  <InlineHelperText
                    variant="INFO"
                    helperText="A site describes where a supply chain activity takes place, like a farm, processing plant or factory."
                  />
                </Box>
                <SiteForm
                  disableSiteSelection={!!defaultSiteType}
                  hidePartnerSelector={hidePartnerSelector}
                  disableRawMaterial={defaultSiteType === ESiteType.FARM && !!defaultRawMaterialId}
                  defaultCountry={defaultCountry}
                />
              </StyledDialogContent>

              <Box mt={7} />
              <StyledDialogActions has-shadow={Booleanish(values.siteType === 'FARM')}>
                <ThemeButton color="BLUE_ICE" size="large" onClick={handleClose}>
                  Cancel
                </ThemeButton>
                <ThemeButton
                  loading={isSubmitting || isExternalIdAvailableLoading}
                  disabled={!isValid}
                  color="PRIMARY"
                  size="large"
                  type="submit"
                  data-testid="submit-site-form-btn"
                >
                  Save
                </ThemeButton>
              </StyledDialogActions>
            </Form>
          )}
        </Formik>
      </Box>
    </DialogDefault>
  );
};

export default AddEditSiteDialog;
